import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import {
    Redirect,
    Route,
    Switch,
    useHistory,
    useLocation,
} from 'react-router-dom';

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import {
    ConnectedRouter,
    connectRouter,
    routerMiddleware,
} from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import thunk from 'redux-thunk';

import { isClientRendering } from '../../lib/helpers';
import { saveInviteEmailParamsToLocalStorage } from './utils';

import eventTracking from '../../services/analytics/eventTracking';
import applyPolyfills from '../../services/polyfill';
import AuthCheckEmail from '../AuthCheckEmail/AuthCheckEmail';
import AuthLayout from '../AuthLayout/AuthLayout';
import { msalConfig } from '../MicrosoftAuthButton/msalConfig';
import AcceptOffers from '../acceptOffers/acceptOffers';
import LoginForm from '../loginForm/loginForm';
import NewLoginForm from '../loginForm/new/NewLoginForm';
import NewRecoverPassForm from '../recoverPassForm/new/NewRecoverPassForm';
import RecoverPassForm from '../recoverPassForm/recoverPassForm';
import NewRegistrationForm from '../registration/components/userRegistrationForm/new/NewRegistrationForm';
import UserRegistrationForm from '../registration/components/userRegistrationForm/userRegistrationForm';
import NewRemindPassForm from '../remindPassForm/new/NewRemindPassForm';
import RemindPassFormContainer from '../remindPassForm/remindPassFormContainer';
import remindPassFormReducer from '../remindPassForm/remindPassFormReducer';
import RegistrationByInvite from '../shortRegistrationForm/new/RegistrationByInvite';
import ShortRegistrationForm from '../shortRegistrationForm/shortRegistrationForm';
import NewVerify2FA from '../verify2FA/new/NewVerify2FA';
import Verify2FAContainer from '../verify2FA/verify2FAContainer';
import verify2FAReducer from '../verify2FA/verify2FAReducer';
import Auth from './auth';
// Auth container & error page
import AuthError from './authError';
import authFeatureFlagsReducer from './authFeatureFlags/authFeatureFlagsReducer';
import { useSetFeatureFlagsEffect } from './authFeatureFlags/hooks/useSetFeatureFlagsEffect';
import ErrorCard from './components/ErrorCard/ErrorCard';
import Loading from './components/Loading/Loading';

const browserHistory = isClientRendering() ? createBrowserHistory() : {};

const middleware = [routerMiddleware(browserHistory), thunk];

const store = createStore(
    combineReducers({
        remindPassForm: remindPassFormReducer,
        verify2FA: verify2FAReducer,
        router: connectRouter(browserHistory),
        authFeatureFlags: authFeatureFlagsReducer,
    }),
    applyMiddleware(...middleware),
);

if (isClientRendering()) {
    applyPolyfills();
}

const msalInstance = new PublicClientApplication(msalConfig);

const AuthComponent = () => {
    const {
        authFeatureFlags,
        requestState: authFeatureFlagsRequestState,
        errorMessage,
    } = useSetFeatureFlagsEffect();

    const history = useHistory();
    const location = useLocation();
    const { pathname, search } = location;

    useEffect(() => {
        saveInviteEmailParamsToLocalStorage(location);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname, search]);

    useEffect(() => {
        function logEvent(loc) {
            if (loc) {
                eventTracking.pageview('', loc.pathname + loc.search);
            }
        }

        return history.listen(logEvent);
    }, [history]);

    if (
        authFeatureFlagsRequestState === 'uncalled' ||
        authFeatureFlagsRequestState === 'pending'
    ) {
        return <Loading />;
    }

    if (authFeatureFlagsRequestState === 'error') {
        return <ErrorCard errorMessage={errorMessage} />;
    }

    return (
        <>
            {authFeatureFlags.NEW_REGISTRATION_FLOW ? (
                <AuthLayout>
                    <Switch>
                        <Route
                            exact
                            path="/auth/check-email"
                            component={AuthCheckEmail}
                        />
                        <Route
                            exact
                            path="/auth/login"
                            component={NewLoginForm}
                        />
                        <Route
                            exact
                            path="/auth/password/remind"
                            component={NewRemindPassForm}
                        />
                        <Route
                            exact
                            path="/auth/password/recover"
                            component={NewRecoverPassForm}
                        />
                        <Route
                            exact
                            path="/auth/2fa/verify"
                            component={NewVerify2FA}
                        />
                        <Route
                            exact
                            path="/auth/registration/register-user"
                            component={NewRegistrationForm}
                        />
                        <Route
                            exact
                            path="/auth/registration/short"
                            component={RegistrationByInvite}
                        />
                        <Route path="/auth/not-found" component={AuthError} />
                        <Redirect
                            from="/auth/registration"
                            to={{
                                pathname: '/auth/registration/register-user',
                                search,
                            }}
                        />
                        <Redirect
                            from="/auth/trial-registration"
                            to="/auth/registration/register-user?activeProTrial=true"
                        />
                        {/* temporary redirect */}
                        <Redirect
                            from="/auth/registration-new"
                            to="/auth/registration"
                        />
                        <Redirect
                            strict
                            from="/auth"
                            to={{
                                pathname: '/auth/check-email',
                                search,
                            }}
                        />
                        <Redirect to="/auth/not-found" />
                    </Switch>
                </AuthLayout>
            ) : (
                <Auth>
                    <Switch>
                        <Route exact path="/auth/login" component={LoginForm} />
                        <Route
                            exact
                            path="/auth/password/remind"
                            component={RemindPassFormContainer}
                        />
                        <Route
                            exact
                            path="/auth/password/recover"
                            component={RecoverPassForm}
                        />
                        <Route
                            exact
                            path="/auth/registration/short"
                            component={ShortRegistrationForm}
                        />
                        <Route
                            exact
                            path="/auth/registration/offers"
                            component={AcceptOffers}
                        />
                        <Route
                            exact
                            path="/auth/2fa/verify"
                            component={Verify2FAContainer}
                        />
                        <Route
                            exact
                            path="/auth/registration/register-user"
                            component={UserRegistrationForm}
                        />
                        <Route path="/auth/not-found" component={AuthError} />
                        <Redirect
                            from="/auth/registration"
                            to="/auth/registration/register-user"
                        />
                        <Redirect
                            from="/auth/trial-registration"
                            to="/auth/registration/register-user?activeProTrial=true"
                        />
                        {/* temporary redirect */}
                        <Redirect
                            from="/auth/registration-new"
                            to="/auth/registration"
                        />
                        <Redirect
                            strict
                            from="/auth"
                            to={{
                                pathname: '/auth/login',
                                search,
                            }}
                        />
                        <Redirect to="/auth/not-found" />
                    </Switch>
                </Auth>
            )}
        </>
    );
};

const authWrapper = () => (
    <Provider store={store}>
        <ConnectedRouter history={browserHistory}>
            <MsalProvider instance={msalInstance}>
                <AuthComponent />
            </MsalProvider>
        </ConnectedRouter>
    </Provider>
);

export default authWrapper;
